import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { AppState } from '@app/store/states/app.states';
import { environment } from 'src/environments/environment';
import * as AppActions from '@app/store/actions/app.actions';
import { selectAppState } from '@app/store/selectors/app.selectors';
import { SessionInfo } from '@app/store/models/session-info.interface';
import { CoreService } from 'src/app/modules/core/services/core.service';
import { PaymentService } from 'src/app/modules/core/services/payment.service';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { FunctionsService } from 'src/app/modules/core/services/functions.service';
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UserQrGeneratorService } from '@app/modules/user/services/user-qr-generator.service';
import { AlaCarteOrderDetails, AlaCarteOrderDetailsForm, CartItemForm } from '@app/store/models/alacarte-order-details.interface';
import { InvoiceStatus, ProductSubscriptionDurations, CouponTypes, AlaCarteCheckoutSession, Cart, CartItem, percentageCalculatorInverse, Pricings, ProductTypes, Statuses, PaymentMethodTypes } from 'shared-library';
import { SeoEventNames, SeoService } from '../../services/seo.service';

declare const Stripe: any

@Component({
  selector: 'app-core-cart',
  templateUrl: './core-cart.component.html',
  styleUrls: ['./core-cart.component.scss']
})
export class CoreCartComponent implements OnInit, OnDestroy {

  alacarte_session: AlaCarteCheckoutSession

  processing = false
  acknowledged = false

  appState$ = this.store.select(selectAppState);
  countries = this.functionsService.getCountries()

  card: any
  paymentError: string
  cardNumberError: string
  stripe = Stripe(environment.stripe_key);
  elements = this.stripe.elements();
  cardHandler = this.onChange.bind(this);
  @ViewChild('cardInfo', { static: true }) cardInfo: HTMLElement;

  discount_form: FormGroup<_DiscountForm>
  checkout_form: FormGroup<AlaCarteOrderDetailsForm>

  session_info: SessionInfo
  private subscriptions: Subscription[] = []

  payment_methods = {
    PAYPAL: PaymentMethodTypes.PAYPAL,
    COUPON: PaymentMethodTypes.COUPON,
    CREDIT_CARD: PaymentMethodTypes.CREDIT_CARD
  }

  checkout_types = {
    more_scans: ProductTypes.more_scans,
    plan_subscription: ProductTypes.plan_subscription,
  }

  applying_discount = false

  constructor(
    private store: Store,
    private router: Router,
    public location: Location,
    private seoService: SeoService,
    public coreService: CoreService,
    private formBuilder: FormBuilder,
    private changeMonitor: ChangeDetectorRef,
    public functionsService: FunctionsService,
    public readonly paymentService: PaymentService,
    private qrGeneratorService: UserQrGeneratorService
  ) {
    this.subscriptions.push(this.coreService.session_dataObservable.subscribe(si => { this.session_info = si; this.ngOnInit() }))
    this.subscriptions.push(this.appState$.subscribe(ls => {
      this.alacarte_session = ls.data.cache.alacarte_session
    }))
  }

  ngOnDestroy(): void {
    this.subscriptions.map(s => s.unsubscribe())
  }

  ngOnInit(): void {
    this.initParams()
    this.functionsService.initializeTooltip()
  }

  ngAfterViewInit(): void {
    this.initStripe()
  }

  initParams(): void {

    if (!this.alacarte_session) return
    if (this.alacarte_session.paid) {
      this.store.dispatch(AppActions.removeAlaCarteSession())
      this.alacarte_session = null
      return
    }

    this.discount_form = this.formBuilder.group({
      code: ["", [Validators.required]]
    })

    const calculateAmount = (): number => {
      let total = 0
      for (const cartItem of this.alacarte_session.cart.items) {
        total += cartItem.unit_price * cartItem.quantity
      }
      return total
    }

    const amount = calculateAmount()
    const tax = percentageCalculatorInverse(this.alacarte_session.tax_percentage, amount)
    const amount_to_pay = amount + tax
    this.checkout_form = this.formBuilder.group({
      recaptcha_token: [""],
      id: [this.alacarte_session.id],
      tax: [tax, Validators.required],
      amount: [amount, Validators.required],
      sortKey: [this.alacarte_session.sortKey],
      amount_to_pay: [amount_to_pay, Validators.required],
      status: [this.alacarte_session.paid ? InvoiceStatus.PAID : InvoiceStatus.PENDING],
      stripe_payment_intent_client_secret: [this.session_info.payment.alacarte_session?.stripe_payment_intent_client_secret],
      invoice_number: [this.alacarte_session.invoice_number],
      payment_method: [this.alacarte_session.payment_method],
      currency: [this.alacarte_session.currency, Validators.required],
      timezone: [this.alacarte_session.timezone, Validators.required],
      billing_address: this.formBuilder.group({
        zip: this.formBuilder.control({ value: this.alacarte_session.billing_address?.zip || this.session_info.payment.alacarte_session?.billing_address.zip, disabled: false }),
        city: this.formBuilder.control({ value: this.alacarte_session.billing_address?.city || this.session_info.payment.alacarte_session?.billing_address.city, disabled: false }, [this.alacarte_session.payment_method === this.payment_methods.CREDIT_CARD && amount_to_pay > 0 ? Validators.required : Validators.nullValidator]),
        state: this.formBuilder.control({ value: this.alacarte_session.billing_address?.state || this.session_info.payment.alacarte_session?.billing_address.state, disabled: false }, [this.alacarte_session.payment_method === this.payment_methods.CREDIT_CARD && amount_to_pay > 0 ? Validators.required : Validators.nullValidator]),
        country: this.formBuilder.control({ value: this.alacarte_session.billing_address?.country || this.session_info.payment.alacarte_session?.billing_address.country, disabled: false }, [this.alacarte_session.payment_method === this.payment_methods.CREDIT_CARD && amount_to_pay > 0 ? Validators.required : Validators.nullValidator]),
        address: this.formBuilder.control({ value: this.alacarte_session.billing_address?.address || this.session_info.payment.alacarte_session?.billing_address.address, disabled: false }, [this.alacarte_session.payment_method === this.payment_methods.CREDIT_CARD && amount_to_pay > 0 ? Validators.required : Validators.nullValidator]),
        full_name: this.formBuilder.control({ value: this.alacarte_session.billing_address?.full_name || this.session_info.payment.alacarte_session?.billing_address.full_name, disabled: false }, [this.alacarte_session.payment_method === this.payment_methods.CREDIT_CARD && amount_to_pay > 0 ? Validators.required : Validators.nullValidator]),
        email: this.formBuilder.control({ value: this.alacarte_session.billing_address?.email || this.session_info.payment.alacarte_session?.billing_address.email || this.alacarte_session.cart.email, disabled: false }, [this.alacarte_session.payment_method === this.payment_methods.CREDIT_CARD && amount_to_pay > 0 ? Validators.required : Validators.nullValidator, Validators.email]),
        mobile: this.formBuilder.control({ value: this.alacarte_session.billing_address?.mobile || this.session_info.payment.alacarte_session?.billing_address.mobile, disabled: false }, this.alacarte_session.payment_method === this.payment_methods.CREDIT_CARD && amount_to_pay > 0 ? [Validators.pattern(this.functionsService.mobileRegex())] : [Validators.nullValidator]),
      }),
      company_details: this.formBuilder.group({
        company_name: [this.session_info.payment.alacarte_session?.company_details.company_name],
        company_city: [this.session_info.payment.alacarte_session?.company_details.company_city],
        company_state: [this.session_info.payment.alacarte_session?.company_details.company_state],
        company_street: [this.session_info.payment.alacarte_session?.company_details.company_street],
        company_country: [this.session_info.payment.alacarte_session?.company_details.company_country],
        company_address: [this.session_info.payment.alacarte_session?.company_details.company_address],
        company_postal_code: [this.session_info.payment.alacarte_session?.company_details.company_postal_code],
        company_website: [this.session_info.payment.alacarte_session?.company_details.company_website, [Validators.pattern(this.functionsService.urlRegex())]],
      }),
      coupons: this.formBuilder.group({
        discount: this.formBuilder.group({
          why: [""],
          code: [""],
          valid: [false],
          discount_amount: [0],
          actual_value_off: [0],
          type: [CouponTypes.DISCOUNT],
          discount_amount_in_percentage: [true],
          product_price_ids: this.formBuilder.array([].map(s => this.formBuilder.control(s))),
        }),
      }),
      cart: this.formBuilder.group({
        id: [this.alacarte_session.cart.id],
        email: [this.alacarte_session.cart.email, [Validators.required, Validators.email]],
        items: this.formBuilder.array(this.alacarte_session.cart.items.map(item => this.formBuilder.group({
          id: [item.id],
          quantity: [item.quantity],
          unit_price: [item.unit_price],
          product_id: [item.product_id],
          product_url: [item.product_url],
          product_title: [item.product_title],
          product_price_id: [item.product_price_id],
          product_description: [item.product_description],
        })))
      }),
    })

    this.subscriptions.push(this.checkout_form.get('cart.email').valueChanges.subscribe(value => {
      const cc7 = this.checkout_form.get('billing_address.email');
      if (this.functionsService.isEmail(value) && (!cc7.value || value.includes(cc7.value))) {
        cc7.setValue(value);
      }
    }))

    this.subscriptions.push(this.checkout_form.get('payment_method').valueChanges.subscribe(value => {
      const cc1 = this.checkout_form.get('billing_address.city');
      const cc7 = this.checkout_form.get('billing_address.email');
      const cc2 = this.checkout_form.get('billing_address.state');
      const cc3 = this.checkout_form.get('billing_address.mobile');
      const cc4 = this.checkout_form.get('billing_address.country');
      const cc5 = this.checkout_form.get('billing_address.address');
      const cc6 = this.checkout_form.get('billing_address.full_name');

      if (value === this.payment_methods.CREDIT_CARD && this.checkout_form.controls.amount_to_pay.value > 0) {
        cc1.setValidators([Validators.required]);
        cc2.setValidators([Validators.required]);
        cc4.setValidators([Validators.required]);
        cc5.setValidators([Validators.required]);
        cc6.setValidators([Validators.required]);
        cc7.setValidators([Validators.required, Validators.email]);
        cc3.setValidators([Validators.pattern(this.functionsService.mobileRegex())]);
      } else {
        cc1.clearValidators();
        cc2.clearValidators();
        cc3.clearValidators();
        cc4.clearValidators();
        cc5.clearValidators();
        cc6.clearValidators();
        cc7.clearValidators();
      }
      cc1.updateValueAndValidity();
      cc2.updateValueAndValidity();
      cc3.updateValueAndValidity();
      cc4.updateValueAndValidity();
      cc5.updateValueAndValidity();
      cc6.updateValueAndValidity();
      cc7.updateValueAndValidity();
    }))

    this.subscriptions.push(this.checkout_form.controls.amount_to_pay.valueChanges.subscribe(value => {
      const cc1 = this.checkout_form.get('billing_address.city');
      const cc7 = this.checkout_form.get('billing_address.email');
      const cc2 = this.checkout_form.get('billing_address.state');
      const cc3 = this.checkout_form.get('billing_address.mobile');
      const cc4 = this.checkout_form.get('billing_address.country');
      const cc5 = this.checkout_form.get('billing_address.address');
      const cc6 = this.checkout_form.get('billing_address.full_name');


      if (this.checkout_form.get('payment_method').value === this.payment_methods.CREDIT_CARD && value > 0) {
        cc1.setValidators([Validators.required]);
        cc2.setValidators([Validators.required]);
        cc4.setValidators([Validators.required]);
        cc5.setValidators([Validators.required]);
        cc6.setValidators([Validators.required]);
        cc7.setValidators([Validators.required, Validators.email]);
        cc3.setValidators([Validators.pattern(this.functionsService.mobileRegex())]);
      } else {
        cc1.clearValidators();
        cc2.clearValidators();
        cc3.clearValidators();
        cc4.clearValidators();
        cc5.clearValidators();
        cc6.clearValidators();
        cc7.clearValidators();
      }
      cc1.updateValueAndValidity();
      cc2.updateValueAndValidity();
      cc3.updateValueAndValidity();
      cc4.updateValueAndValidity();
      cc5.updateValueAndValidity();
      cc6.updateValueAndValidity();
      cc7.updateValueAndValidity();
    }))

    this.processing = false
  }

  removeCartItem(index: number): void {
    this.checkout_form.controls.cart.controls.items.removeAt(index)
    this.store.dispatch(AppActions.removeCartInAlaCarteSession({ index }))
    this.calculatePayment()
  }

  updateCartItemQuantity(item: FormGroup<CartItemForm>, index: number, is_increment = true): void {
    item.controls.quantity.setValue(is_increment ? item.controls.quantity.value + 1 : item.controls.quantity.value - 1)
    this.store.dispatch(AppActions.updateCartInAlaCarteSession({ data: item.value as CartItem, index }))
    this.calculatePayment()
  }

  private calculatePayment(): void {

    const calculateAmount = () => {
      let response = { amount: 0, tax: 0, amount_to_pay: 0, discount: 0 }
      for (const cartItem of this.checkout_form.controls.cart.controls.items.controls) {
        response.amount += cartItem.controls.unit_price.value * cartItem.controls.quantity.value

        if (this.checkout_form.get('coupons.discount.valid').value && this.checkout_form.get('coupons.discount.product_price_ids').value.includes(cartItem.controls.product_price_id.value)) {
          let actual_value_off = this.checkout_form.controls.coupons.controls.discount.controls.discount_amount.value
          if (this.checkout_form.get('coupons.discount.discount_amount_in_percentage').value) {
            actual_value_off = this.functionsService.percentageCalculator(actual_value_off, response.amount)
          }
          response.discount += actual_value_off
        }
      }

      response.tax = percentageCalculatorInverse(this.alacarte_session.tax_percentage, (response.amount - response.discount))
      response.amount_to_pay = ((response.amount - response.discount) + response.tax)

      return response
    }

    const { amount, tax, amount_to_pay, discount } = calculateAmount()

    this.checkout_form.get('tax').setValue(tax)
    this.checkout_form.get('amount').setValue(amount)
    this.checkout_form.get('amount_to_pay').setValue(amount_to_pay)
    this.checkout_form.get("coupons.discount.actual_value_off").setValue(discount)
    if (this.checkout_form.controls.amount_to_pay.value < 0) {
      this.checkout_form.get('amount_to_pay').setValue(0)
    }
  }

  async applyCoupon() {

    if (this.applying_discount) return
    const type: CouponTypes.DISCOUNT = CouponTypes.DISCOUNT
    this.discount_form.markAllAsTouched()
    if (!this.discount_form.valid) return
    this.applying_discount = true

    try {

      // this.seoService.eventEmitter(SeoEventNames.UserActions, "applyCoupon", type, {
      //   code: this.discount_form.controls.code.value,
      // })

      const { data: check_result } = await this.paymentService.applyDiscountToAlaCarteItems({
        id: this.checkout_form.controls.id.value,
        sortKey: this.checkout_form.controls.sortKey.value,
        coupons: {
          discount: {
            discount_amount: 0,
            actual_value_off: 0,
            discount_amount_in_percentage: false,
            code: this.discount_form.controls.code.value,
          }
        },
        cart: this.checkout_form.controls.cart.value as Cart,
        payment_method: this.checkout_form.controls.payment_method.value
      })

      const valid_results = check_result.filter(({ valid }) => valid)
      if (valid_results.length) {
        this.checkout_form.get("coupons.discount.code").setValue(valid_results[0].code)
        this.checkout_form.get("coupons.discount.valid").setValue(valid_results[0].valid)
        this.checkout_form.get("coupons.discount.discount_amount").setValue(valid_results[0].discount_amount)
        this.checkout_form.get("coupons.discount.discount_amount_in_percentage").setValue(valid_results[0].discount_amount_in_percentage)
        this.checkout_form.controls.coupons.controls.discount.setControl('product_price_ids', this.formBuilder.array(valid_results.map(({ product_price_id }) => this.formBuilder.control(product_price_id))));
        this.calculatePayment()
        this.applying_discount = false
        return
      }
      this.resetCoupon(type)
      this.checkout_form.get("coupons.discount.why").setValue(check_result[0]?.why || "Invalid code")
    } catch (error) {
      this.functionsService.logToConsole(error)
    }
    this.applying_discount = false
  }

  async startPayment() {
    //User clicked pay
    if (this.processing) {
      return
    }
    this.checkout_form.markAllAsTouched()
    if (!this.checkout_form.valid) {
      this.functionsService.logToConsole(this.checkout_form)
      this.qrGeneratorService.scrollToFirstInvalidControl()
      return
    }

    this.session_info.payment.alacarte_session = this.checkout_form.value as AlaCarteOrderDetails
    // this.seoService.eventEmitter(SeoEventNames.UserActions, "startPayment", "alaCarte", {
    //   session_id: this.session_info.payment.alacarte_session.id,
    // })

    this.processing = true
    this.coreService.setSessionData(this.session_info, false)
    //all good
    try {
      this.session_info.payment.alacarte_session.recaptcha_token = await this.coreService.getRecaptchaToken()
      const response = await this.paymentService.initialiseAlaCarteSessionPayment(this.session_info.payment.alacarte_session)
      this.session_info.payment.alacarte_session.invoice_number = response.data.invoice_number
      this.store.dispatch(AppActions.initialiseAlaCarteSessionSuccess({ response }))
      if (response.data.paid) {
        this.paymentSuccessFulFormation()
        return
      }
      this.session_info.payment.alacarte_session.stripe_payment_intent_client_secret = response.data.client_secret
      this.coreService.setSessionData(this.session_info, false)
      this.pay()
    } catch (error) {
      this.processing = false
    }
  }

  resetCoupon(type: CouponTypes.DISCOUNT = CouponTypes.DISCOUNT): void {
    this.checkout_form.get("coupons.discount.why").setValue("")
    this.checkout_form.get("coupons.discount.code").setValue("")
    this.checkout_form.get("coupons.discount.type").setValue(type)
    this.checkout_form.get("coupons.discount.valid").setValue(false)
    this.checkout_form.get("coupons.discount.discount_amount").setValue(0.00)
    this.checkout_form.get("coupons.discount.discount_amount_in_percentage").setValue(false)
    this.checkout_form.controls.coupons.controls.discount.setControl('product_price_ids', this.formBuilder.array([].map(({ product_price_id }) => this.formBuilder.control(product_price_id))));
    this.calculatePayment()
  }

  onChange({ error }) {
    this.cardNumberError = ""
    if (error) {
      this.cardNumberError = error.message
    }
    this.changeMonitor.detectChanges()
  }

  initStripe(): void {
    if (!this.cardInfo) {
      this.cardInfo = document.getElementById('card-element')
      if (!this.cardInfo) {
        return
      }
    }

    const style = {
      base: {
        iconColor: '#DD2B26',
        color: '#000000',
        lineHeight: '20px',
        fontWeight: 300,
        fontSize: '16px',
        '::placeholder': {
          color: '#f29893',
        },
      },
      invalid: {
        iconColor: 'red',
        color: 'red',
      },
    };
    this.card = this.elements.create("card", { style });
    this.card.mount(this.cardInfo)
    this.card.addEventListener('change', this.cardHandler)
  }

  pay(): void {
    this.stripe.confirmCardPayment(this.session_info.payment.alacarte_session.stripe_payment_intent_client_secret, {
      payment_method: {
        card: this.card
      },
      setup_future_usage: 'off_session',
    }).then((result: any) => {
      this.setOutCome(result, this)
    });
  }

  async setOutCome(result: any, _this: CoreCartComponent) {
    if (result.error) {
      _this.processing = false
      _this.cardNumberError = result.error.message
      return
    }

    if (result.paymentIntent.status === 'requires_action' && result.paymentIntent.next_action) {
      if (result.paymentIntent.next_action.type == 'redirect_to_url') {
        window.location.href = result.paymentIntent.next_action.redirect_to_url.url;
      }
    }

    if (result.paymentIntent.status === 'succeeded') {
      await _this.paymentSuccessFulFormation()
      _this.processing = false
      _this.store.dispatch(AppActions.initialiseAlaCarteSessionSuccess({ response: { data: { ..._this.alacarte_session, paid: true, status: Statuses.INACTIVE } } }))
      return
    }

    _this.functionsService.showErrorMessage("An unknown error occurred while processing your payment, please try again later")
    _this.processing = false
  }

  async paymentSuccessFulFormation() {
    await new Promise((y) => {
      setTimeout(() => {
        y(true)
      }, 2000)
    })

    // this.seoService.eventEmitter(SeoEventNames.Purchase, "paymentSuccess", "alaCarte", {
    //   session_id: this.session_info.payment.alacarte_session.id,
    // })

    this.session_info.payment.alacarte_session.status = InvoiceStatus.PAID
    this.coreService.setSessionData(this.session_info, false)
    this.router.navigateByUrl(`/cart/payment/success`)
  }

  getPercentageToSave(app_state: AppState, price: Pricings): number {
    const monthly_price = app_state.data.cache.products.plans.find(plan => plan.plan.durations.in === ProductSubscriptionDurations.MONTH && plan.product_id === price.product_id)
    if (!monthly_price || price.plan.durations.in !== ProductSubscriptionDurations.YEAR) return 0
    const price_estimated = monthly_price.price * 12
    return parseInt(this.functionsService.percentageCalculatorV2(price_estimated - price.price, price.price).toString())
  }
}

interface _DiscountForm {
  code: FormControl<string>
}
